import React from 'react';

import { cssMap } from '@atlaskit/css';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import AvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
import { Inline, Pressable, Text, Box } from '@atlaskit/primitives/compiled';

import type { ProfilePictureInfo } from '../helpers/repliesHelpers';

const styles = cssMap({
	dot: {
		height: '8px',
		width: '8px',
		backgroundColor: token('color.background.brand.bold'),
		borderRadius: token('radius.full'),
		margin: token('space.050'),
	},
	pressable: {
		width: '100%',
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral.subtle'),
		padding: token('space.100'),
		borderRadius: token('radius.small'),
		userSelect: 'none',
		fontWeight: token('font.weight.medium'),
		'&:hover': {
			cursor: 'pointer',
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

const SHOW_HIDDEN_REPLIES = 'show-hidden-replies';

export type RepliesTogglerButtonProps = {
	showHiddenReplies: (e: React.MouseEvent<HTMLElement>) => void;
	profilePicturesHiddenReplies: ProfilePictureInfo[];
	hasHiddenUnreadReplies: boolean;
	analyticsContext: Record<string, any>;
	message: string;
};

export const RepliesTogglerButton = ({
	showHiddenReplies,
	profilePicturesHiddenReplies,
	hasHiddenUnreadReplies,
	analyticsContext,
	message,
}: RepliesTogglerButtonProps) => (
	<Pressable
		onClick={showHiddenReplies}
		xcss={styles.pressable}
		analyticsContext={analyticsContext}
		testId={SHOW_HIDDEN_REPLIES}
		interactionName={SHOW_HIDDEN_REPLIES}
	>
		<Inline alignBlock="center" space="space.100" spread="space-between">
			<Inline alignBlock="center" space="space.100">
				<GrowVerticalIcon label="" />
				{/* Box is necessary to prevent leaky styles being applied to AvatarGroup */}
				<Box as="span">
					<AvatarGroup
						onMoreClick={() => {}}
						data={profilePicturesHiddenReplies.map(({ displayName, profilePicturePath }) => ({
							name: displayName,
							src: profilePicturePath,
						}))}
						maxCount={3}
						appearance="stack"
						size="small"
						shouldPopupRenderToParent
						testId="replies-toggler-profile-pictures"
					/>
				</Box>
				<Text color="color.text.subtle" weight="medium">
					{message}
				</Text>
			</Inline>
			{hasHiddenUnreadReplies && (
				<Box as="span" xcss={styles.dot} testId="unread-comment-replies-indicator" />
			)}
		</Inline>
	</Pressable>
);
